import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal, Search } from "lucide-react";

const CATEGORIES = [
  { id: "", label: "All" },
  { id: "phones", label: "Phones" },
  { id: "laptops", label: "Laptops" },
  { id: "audio", label: "Audio" },
  { id: "wearables", label: "Wearables" },
  { id: "tv", label: "TV" },
  { id: "gaming", label: "Gaming" },
  { id: "accessories", label: "Accessories" },
];

export default function Shop() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialCategory = params.get("category") || "";
  const [category, setCategory] = useState(initialCategory);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("relevance");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setCategory(params.get("category") || ""); }, [params]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (q) qs.set("q", q);
    api.get(`/products?${qs.toString()}`)
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, [category, q]);

  const handleAdd = async (p) => {
    if (!user) { toast.error("Please sign in to add items"); navigate("/login"); return; }
    try { await add(p.product_id, 1); toast.success(`${p.name} added`); }
    catch { toast.error("Could not add to cart"); }
  };

  const sorted = useMemo(() => {
    const a = [...items];
    if (sort === "price_asc") a.sort((x, y) => x.price - y.price);
    if (sort === "price_desc") a.sort((x, y) => y.price - x.price);
    if (sort === "rating") a.sort((x, y) => (y.rating || 0) - (x.rating || 0));
    return a;
  }, [items, sort]);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="shop-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-8">
        <div>
          <p className="vm-overline">Catalog</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-2 tracking-tighter">
            All electronics
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-72">
            <Search className="h-4 w-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              className="vm-input pl-9"
              placeholder="Search products"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="shop-search"
            />
          </div>
          <div className="relative">
            <SlidersHorizontal className="h-4 w-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              className="vm-input pl-9 appearance-none pr-8"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="shop-sort"
            >
              <option value="relevance">Relevance</option>
              <option value="price_asc">Price: low → high</option>
              <option value="price_desc">Price: high → low</option>
              <option value="rating">Top rated</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1">
        {CATEGORIES.map((c) => (
          <button
            key={c.id || "all"}
            onClick={() => {
              setCategory(c.id);
              navigate(c.id ? `/shop?category=${c.id}` : "/shop");
            }}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
              category === c.id
                ? "bg-volt-500 border-volt-500 text-white shadow-[0_0_18px_rgba(0,85,255,0.35)]"
                : "bg-transparent border-white/10 text-neutral-400 hover:text-white hover:border-white/30"
            }`}
            data-testid={`shop-category-${c.id || "all"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="vm-card aspect-[3/4] animate-pulse bg-ink-800" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="vm-card p-12 text-center" data-testid="shop-empty">
          <p className="font-display text-2xl font-bold">No products found</p>
          <p className="text-neutral-400 mt-2">Try a different category or search term.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {sorted.map((p) => (
            <ProductCard key={p.product_id} product={p} onAdd={handleAdd} />
          ))}
        </div>
      )}
    </div>
  );
}
